// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import * as React from 'react';

import Select2 from 'wdna-select2';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { system } from '../requests';

type Props = unknown;

interface State {
  update?: any;
  loading: boolean;
  data1?: any[];
  data2?: any[];
  logLevel: string;
  logLines?: number;
  modalTitle?: string;
  modalShow?: boolean;
  modalContent?: string;
  showGrid?: boolean;
}

export default class System extends React.Component <Props, State> {
  private update: boolean;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;
    this.update = false;

    this.state = {
      update: window.getStr(),
      loading: false,
      data1: [],
      data2: [],

      logLevel: 'ALL',
      logLines: 1000,

      modalTitle: '',
      modalShow: false,
      modalContent: '',

      showGrid: false,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const data1: string[][] = await system<string[][]>('proc-list') || [];
      data1.shift();
      const files: string[] = await system<string[]>('log-list') || [];
      const data2: any[] = files.map((item: string, index: any) => { return { id: index, name: item, }; });
      this.setLocalState({
        data1,
        data2,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onInfo = (value: any): void => {
    const { logLevel, logLines } = this.state;
    this.loadingStart();
    const req: Record<string, any> = {
      filter: '1',
      lines: 0
    };
    if (logLevel !== 'ALL') req.filter = logLevel;
    if (logLines) req.lines = logLines;

    system('log-content', value.name, req)
      ?.then((resp: any) => {
        this.setLocalState({
          update: window.getStr(),
          modalShow: true,
          modalContent: resp,
          modalTitle: value.name,
        });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public onRefresh = (): void => {
    this.loadingStart();
    system('proc-list')
      ?.then((resp: any[]) => {
        resp.shift();
        this.setLocalState({
          data1: resp
        });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public onCloseModal = (): void => {
    this.setLocalState({
      update: window.getStr(),
      modalShow: false,
    });
  };

  public handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setLocalState({
      [name]: value,
    }, () => {
      this.onInfo({ name: this.state.modalTitle });
    });
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, showGrid, data1, data2, modalShow, modalTitle, modalContent, logLevel, logLines } = this.state;

    const columnAlias1: any = {
      0: 'PID',
      1: '%CPU',
      2: '%MEM',
      3: 'SIZE',
      4: 'ELAPSED',
      5: 'CMD',
    };
    const columnsToHide1: any = [];
    const columnStyles1: any = {
      0: { textAlign: 'right' },
      1: { textAlign: 'right' },
      2: { textAlign: 'right' },
      3: { textAlign: 'right' },
      4: { textAlign: 'center' },
      5: { textAlign: 'left' },
    };
    const customColumnsDefs1: any = {
      0: { suppressSizeToFit: false, minWidth: 80, maxWidth: 80 },
      1: { suppressSizeToFit: false, minWidth: 80, maxWidth: 80 },
      2: { suppressSizeToFit: false, minWidth: 80, maxWidth: 80 },
      3: { suppressSizeToFit: false, minWidth: 80, maxWidth: 80 },
      4: { suppressSizeToFit: false, minWidth: 120, maxWidth: 120 },
    };

    const columnAlias2: any = {
      id: 'Id',
      name: 'File',
    };
    const columnsToHide2: any = ['id'];
    const columnStyles2: any = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs2: any = {
      id: { suppressSizeToFit: false, minWidth: 50, maxWidth: 50 },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <header>
              <h2>
                <span className="widget-icon" style={{ padding: '8px' }}>
                  <i className="fa fa-edit" />
                </span>
                Process info
              </h2>
            </header>
            <Table
              data={data1 || []}
              alias={columnAlias1}
              hiddenCols={columnsToHide1}
              customCellStyles={columnStyles1}
              customColumnsDefs={customColumnsDefs1}
              buttons={{ refresh: true }}
              onRefresh={this.onRefresh}
              loading={loading}
              rowDataUpdated={this.rowDataUpdated}
              pagination={false}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <header>
              <h2>
                <span className="widget-icon" style={{ padding: '8px' }}>
                  <i className="fa fa-edit" />
                </span>
                Log files
              </h2>
            </header>
            <Table
              data={data2 || []}
              alias={columnAlias2}
              hiddenCols={columnsToHide2}
              customCellStyles={columnStyles2}
              customColumnsDefs={customColumnsDefs2}
              buttons={{ info: true, export: false, filters: false, reset: false }}
              onInfo={this.onInfo}
              loading={loading}
              rowDataUpdated={this.rowDataUpdated}
              pagination={false}
              showRowNumber
            />
          </div>
        </div>
        <ContainerModal
          fullScreenButton={false}
          show={modalShow}
          title={`LOG FILE - ${modalTitle || ''}`}
          onClose={this.onCloseModal}
          modalClass="modal-lg"
          footerButtons={[
            {
              text: 'OK',
              className: 'btn btn-primary',
              onClick: this.onCloseModal,
            },
          ]}
        >
          <div className="row" style={{ padding: '15px' }}>
            <div className="col-3">
              <label className="label">
                Level:
              </label>
              <Select2
                name="logLevel"
                id="logLevel"
                data={[
                  { id: 'ALL', text: 'ALL' },
                  { id: 'WARN', text: 'WARNING' },
                  { id: 'ERRO', text: 'ERROR' },
                ]}
                value={_.defaultTo(logLevel, '')}
                onChange={this.handleChangeFilter}
                style={{ width: '75%' }}
              />
            </div>
            <div className="col-3">
              <label className="label">
                Lines:
              </label>
              <Select2
                name="logLines"
                id="logLines"
                data={[
                  { id: 1000, text: '1000' },
                  { id: 2000, text: '2000' },
                  { id: 5000, text: '5000' },
                ]}
                value={_.defaultTo(logLines, '')}
                onChange={this.handleChangeFilter}
                style={{ width: '75%' }}
              />
            </div>
          </div>
          <b>File Content: </b><br />
          <textarea
            className="input-new"
            style={{ width: '100%', fontFamily: 'Inconsolata, monospace', fontSize: '11px' }}
            rows={25}
            cols={500}
            value={_.defaultTo(modalContent, '')}
            readOnly
          />
        </ContainerModal>
      </div>
    );
  }
}
